import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Truck, Clock3, CheckCircle2, XCircle, Users, Check, ChevronsUpDown, Loader2, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Shared Vehicle-Change + Assistants picker used by drivers and internal-staff.
 *  Assistants come from the standalone `/api/assistants-registry` roster and can
 *  only be claimed by one user at a time (1:1 exclusive). */
export default function AssistantsPicker({ testIdPrefix = 'staff' }) {
  const [reqs, setReqs] = useState([]);
  const [roster, setRoster] = useState([]); // pickable assistants
  const [fleet, setFleet] = useState([]);
  const [currentReg, setCurrentReg] = useState('');
  const [loading, setLoading] = useState(true);

  const [showReq, setShowReq] = useState(false);
  const [newReg, setNewReg] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const [openPicker, setOpenPicker] = useState(false);
  const [savingId, setSavingId] = useState('');

  const tid = (suffix) => `${testIdPrefix}-${suffix}`;

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, me] = await Promise.all([
        axios.get(`${API}/my-vehicle-change-requests`),
        axios.get(`${API}/assistants-registry/pickable`),
        axios.get(`${API}/fleet-registrations`),
        axios.get(`${API}/auth/me`).catch(() => ({ data: {} })),
      ]);
      setReqs(r1.data || []);
      setRoster(r2.data || []);
      setFleet(r3.data || []);
      setCurrentReg((me.data?.assigned_vehicle_registration || '').toUpperCase());
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!newReg.trim()) return toast.error('Pick a vehicle');
    if (!reason.trim()) return toast.error('Reason required');
    setBusy(true);
    try {
      await axios.post(`${API}/vehicle-change-requests`, {
        new_registration: newReg.trim().toUpperCase(),
        reason: reason.trim(),
      });
      toast.success('Request submitted — admin will approve shortly');
      setShowReq(false); setNewReg(''); setReason('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const toggle = async (a) => {
    setSavingId(a.id);
    try {
      if (a.is_mine) {
        await axios.post(`${API}/assistants-registry/${a.id}/release`);
        toast.success(`Released ${a.full_name}`);
      } else {
        await axios.post(`${API}/assistants-registry/${a.id}/claim`);
        toast.success(`${a.full_name} added as your assistant`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSavingId(''); }
  };

  const pending = reqs.find((r) => r.status === 'pending');
  const linkedList = useMemo(() => roster.filter((a) => a.is_mine), [roster]);
  const availableFleet = useMemo(
    () => fleet.filter((v) => (v.registration || '').toUpperCase() !== currentReg),
    [fleet, currentReg],
  );

  return (
    <div className="space-y-3" data-testid={tid('vehicle-assistants')}>
      {/* Vehicle change */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Truck className="h-4 w-4 text-indigo-600" /> Vehicle Change
            </CardTitle>
            <CardDescription className="text-xs">Moved to a different vehicle? Send admin a change request.</CardDescription>
          </div>
          <Dialog open={showReq} onOpenChange={(o) => { setShowReq(o); if (!o) { setNewReg(''); setReason(''); } }}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={!!pending} data-testid={tid('request-vehicle-change-btn')}>
                <Truck className="h-3 w-3 mr-1" /> Request Vehicle Change
              </Button>
            </DialogTrigger>
            <DialogContent data-testid={tid('vc-request-dialog')}>
              <DialogHeader>
                <DialogTitle>Request vehicle change</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">New Vehicle *</Label>
                  <Select value={newReg} onValueChange={setNewReg}>
                    <SelectTrigger data-testid={tid('vc-new-reg-trigger')} className="w-full">
                      <SelectValue placeholder={availableFleet.length ? 'Pick a fleet vehicle…' : 'No other vehicles found'} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableFleet.map((v) => (
                        <SelectItem key={v.registration} value={v.registration} data-testid={tid(`vc-reg-opt-${v.registration}`)}>
                          {v.registration}{v.make_model ? ` — ${v.make_model}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {currentReg && (
                    <div className="text-[11px] text-muted-foreground mt-1">Current: <b>{currentReg}</b></div>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Reason *</Label>
                  <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why are you changing vehicle?" data-testid={tid('vc-reason')} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowReq(false)}>Cancel</Button>
                <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid={tid('vc-submit')}>
                  {busy ? 'Saving…' : 'Submit'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="pt-0">
          {pending ? (
            <div className="rounded border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2 flex items-center gap-2" data-testid={tid('vc-pending-banner')}>
              <Clock3 className="h-3 w-3" />
              Pending: {pending.current_registration || 'none'} → <b>{pending.new_registration}</b>. Admin will review shortly.
            </div>
          ) : reqs.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No requests yet.</p>
          ) : (
            <div className="space-y-1 text-xs">
              {reqs.slice(0, 3).map((r) => (
                <div key={r.id} className="flex items-center justify-between border-t pt-1">
                  <span>{r.current_registration || 'none'} → <b>{r.new_registration}</b></span>
                  <span>
                    {r.status === 'approved' ? <Badge className="bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" />Approved</Badge>
                      : <Badge className="bg-rose-600"><XCircle className="h-3 w-3 mr-1" />Declined</Badge>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assistants */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4 text-emerald-600" /> My Assistant(s)
            </CardTitle>
            <CardDescription className="text-xs">Pick the assistant(s) who ride along with you. An assistant can only be linked to one person at a time — admin manages the roster.</CardDescription>
          </div>
          <Popover open={openPicker} onOpenChange={setOpenPicker}>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-8 min-w-[180px] justify-between"
                data-testid={tid('assistant-select-trigger')}
                disabled={loading}
              >
                <span className="truncate text-xs">
                  {loading ? 'Loading…'
                    : linkedList.length === 0 ? 'Select assistants…'
                    : linkedList.length === 1 ? linkedList[0].full_name
                    : `${linkedList.length} linked`}
                </span>
                <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="end" data-testid={tid('assistant-select-content')}>
              <div className="max-h-72 overflow-y-auto p-1">
                {roster.length === 0 ? (
                  <div className="p-3 text-xs text-muted-foreground">No assistants available. Ask an admin to add some.</div>
                ) : (
                  roster.map((a) => {
                    const locked = !a.is_free && !a.is_mine;
                    const busyRow = savingId === a.id;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => !locked && !busyRow && toggle(a)}
                        disabled={locked || busyRow}
                        className={`w-full flex items-center justify-between px-2 py-1.5 text-xs rounded ${locked ? 'opacity-60 cursor-not-allowed' : 'hover:bg-slate-100'}`}
                        data-testid={tid(`assistant-opt-${a.id}`)}
                      >
                        <span className="truncate text-left">
                          <span className="font-medium">{a.full_name}</span>
                          {locked && <span className="ml-1 text-[10px] text-amber-700">(taken by {a.taken_by})</span>}
                        </span>
                        {busyRow ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />
                          : a.is_mine ? <Check className="h-3.5 w-3.5 text-emerald-600" />
                          : locked ? <Lock className="h-3 w-3 text-amber-600" />
                          : null}
                      </button>
                    );
                  })
                )}
              </div>
              <div className="border-t p-2 flex items-center justify-end">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => setOpenPicker(false)}
                  data-testid={tid('assistant-close-btn')}
                >Close</Button>
              </div>
            </PopoverContent>
          </Popover>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-xs text-muted-foreground">Loading…</div>
          ) : linkedList.length === 0 ? (
            <div className="text-xs text-muted-foreground italic" data-testid={tid('assistants-empty')}>
              No assistants linked yet. Use the picker above to add one.
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid={tid('assistants-linked')}>
              {linkedList.map((a) => (
                <Badge key={a.id} className="bg-emerald-600 text-white text-[11px]" data-testid={tid(`assistant-chip-${a.id}`)}>
                  {a.full_name}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
